import React from 'react';
import { get } from 'lodash-es';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { CmsModel } from 'client/data/models/cms';
import { buildAuthorArticlesPath, buildAuthorVideosPath, EditorialModel } from 'client/data/models/editorial';
import { create404Error } from 'site-modules/shared/utils/errors';
import { getModelState } from 'client/data/luckdragon/model';
import { parseContent } from 'client/data/cms/content';
import { getMeta } from 'site-modules/shared/utils/seo-helper';
import { getProfilePageSchema } from 'client/seo/schemas/profile-page';
import { getListItemSchema } from 'client/seo/schemas/list';
import { getPersonSchema } from 'client/seo/schemas/person';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { getEditorialNewsArticleSchema } from 'client/seo/schemas/article';
import { getEditorialVideoSchema } from 'client/seo/schemas/video';
import { getAuthorContentPath } from 'site-modules/editorial/utils/content-path';
import { getAuthorPageBreadcrumbs } from 'site-modules/editorial/utils/breadcrumbs';
import { ARTICLE_PAGE_SIZE } from 'site-modules/editorial/components/author-article-page/constants';
import { Author } from './author';

export const pageOptions = {
  name: 'other_about_us_author_index',
  category: 'other_about_us_author_index',
  preload: (store, props) => {
    const author = get(props, ['match', 'params', 'author']);

    const preloader = new ModelPreloader(store);

    preloader.resolve(`content["${getAuthorContentPath(author)}"]`, CmsModel);

    preloader.resolve(buildAuthorArticlesPath(author, 1, ARTICLE_PAGE_SIZE), EditorialModel);
    preloader.resolve(buildAuthorVideosPath(author, 1, ARTICLE_PAGE_SIZE), EditorialModel);

    return preloader.load().then(modelState => {
      const content = modelState.get(`content["${getAuthorContentPath(author)}"]`, CmsModel);
      const contentId = get(content, 'id');

      if (!contentId) {
        throw create404Error();
      }

      return preloader.load();
    });
  },
  seo: (state, props) => {
    const modelState = getModelState(state);
    const pathname = get(props, 'location.pathname');
    const author = get(props, ['match', 'params', 'author']);
    const content = modelState.get(`content["${getAuthorContentPath(author)}"]`, CmsModel);

    if (!content) {
      return {};
    }

    const articles = get(
      modelState.get(buildAuthorArticlesPath(author, 1, ARTICLE_PAGE_SIZE), EditorialModel),
      'results',
      []
    );
    const videos = get(
      modelState.get(buildAuthorVideosPath(author, 1, ARTICLE_PAGE_SIZE), EditorialModel),
      'results',
      []
    );

    const parsedContent = parseContent(content);
    const meta = getMeta(parsedContent);
    const { canonical } = meta;
    const authorEntry = parsedContent.child('author');
    const { authorImage, authorTitle, authorName, authorBio } = authorEntry.getAllMetadata();

    return {
      ...meta,
      title: `${authorName}, Automotive Journalist at Edmunds`,
      description: `Articles and videos created by ${authorName} for Edmunds.`,
      jsonld: [
        getProfilePageSchema({ name: authorName, image: authorImage, description: authorBio }),
        getListItemSchema({
          items: articles.map(({ relativeUrl, title }, ind) => ({
            position: ind + 1,
            name: title,
            url: `https://www.edmunds.com${relativeUrl}`,
          })),
          name: `${authorName}'s articles and videos`,
          pageUrl: canonical,
        }),
        getPersonSchema({ name: authorName, jobTitle: authorTitle, pathname, photoUrl: authorImage }),
        getBreadcrumbSchema(getAuthorPageBreadcrumbs(authorName, pathname)),
        ...articles.map(article => getEditorialNewsArticleSchema({ article, author })),
        ...videos.map(video => getEditorialVideoSchema(video)),
      ],
    };
  },
  component: props => <Author {...props} author={get(props, ['match', 'params', 'author'])} />,
};

export default pageDefinition(pageOptions);
